import type { CityConfig } from "../city-template/types";

export const concordConfig: CityConfig = {
  city: "Concord",
  state: "California",
  stateAbbr: "CA",
  slug: "concord",
  route: "/cities/concord",
  title: "Emergency Passport Concord, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Concord, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Contra Costa County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Concord, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Concord and Contra Costa County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Concord%20California%20cityscape%20at%20golden%20hour%20with%20suburban%20downtown%20skyline%2C%20modern%20buildings%20mixed%20with%20green%20hills%20and%20Mount%20Diablo%20in%20the%20background%2C%20warm%20sunset%20light%20over%20Contra%20Costa%20County%2C%20clean%20and%20modern%20suburban%20scene%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20urban%20environment&width=1800&height=700&seq=concord-hero-001&orientation=landscape",
  heroImageAlt: "Concord California cityscape at golden hour with Mount Diablo in the background and Contra Costa County suburban skyline",
  geoPosition: "37.9780;-122.0311",
  geoPlacename: "Concord, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown SF — approximately 30–45 minutes from Concord via I-80",
  },
  nearbyCitiesZips: [
    { name: "Concord", zips: "94518–94521" },
    { name: "Walnut Creek", zips: "94595, 94596, 94597, 94598" },
    { name: "Pleasant Hill", zips: "94523" },
    { name: "Martinez", zips: "94553" },
    { name: "Lafayette", zips: "94549" },
    { name: "Clayton", zips: "94517" },
    { name: "Pittsburg", zips: "94565" },
    { name: "Antioch", zips: "94509, 94531" },
    { name: "Brentwood", zips: "94513" },
    { name: "San Ramon", zips: "94582, 94583" },
    { name: "Dublin", zips: "94568" },
    { name: "Richmond", zips: "94801, 94804, 94805" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Contra Costa County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Berkeley", route: "/cities/berkeley" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "Daly City", route: "/cities/daly-city" },
    { name: "Santa Clara", route: "/cities/santa-clara" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Concord remotely via secure courier. We do not have a walk-in office in Concord — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Contra Costa County travelers.",
  fpcOfficeDistance: "Serving all of Contra Costa County",
  serviceAreaIntro: "From downtown Concord to the Clayton Valley, from the Buchanan Field area to the Todos Santos Plaza — Fast Passport Center serves the entire Concord community. We regularly help travelers from Walnut Creek, Pleasant Hill, Martinez, and Lafayette get their passports fast.",
  nearbyCitiesIntro: "We serve all Concord neighborhoods and nearby Contra Costa County cities including Walnut Creek, Pleasant Hill, Martinez, Lafayette, Clayton, Pittsburg, Antioch, and Brentwood. If you are in the East Bay, we have you covered.",
  howItWorksIntro: "Most Concord travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Oakland International Airport (OAK) and San Francisco International (SFO) nearby, East Bay travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Concord and Contra Costa County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Concord? Here are the most common ones we hear from Contra Costa County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Contra Costa County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rebecca M.",
      location: "Concord, CA",
      rating: 5,
      text: "My business trip to London was in 4 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my career opportunity.",
      service: "Emergency Passport",
    },
    {
      name: "Thomas H.",
      location: "Walnut Creek, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Angela K.",
      location: "Pleasant Hill, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Daniel R.",
      location: "Lafayette, CA",
      rating: 5,
      text: "As a frequent international traveler from the East Bay, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "concord-1",
      name: "Concord Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2720 Oak Rd",
      city: "Concord",
      state: "CA",
      zip: "94519",
      phone: "(925) 676-0204",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from downtown Concord",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "concord-2",
      name: "Walnut Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2070 Broadway",
      city: "Walnut Creek",
      state: "CA",
      zip: "94596",
      phone: "(925) 935-6511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Walnut Creek",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "concord-3",
      name: "Pleasant Hill Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1351 Contra Costa Blvd",
      city: "Pleasant Hill",
      state: "CA",
      zip: "94523",
      phone: "(925) 685-0378",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Pleasant Hill",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for central Contra Costa residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "concord-4",
      name: "Martinez Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3070 Alhambra Ave",
      city: "Martinez",
      state: "CA",
      zip: "94553",
      phone: "(925) 228-4000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Martinez",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Contra Costa residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "concord-5",
      name: "Antioch Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 W 2nd St",
      city: "Antioch",
      state: "CA",
      zip: "94509",
      phone: "(925) 757-6111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Antioch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves eastern Contra Costa County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "concord-6",
      name: "Contra Costa County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "555 Escobar St",
      city: "Martinez",
      state: "CA",
      zip: "94553",
      phone: "(925) 335-7900",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Martinez City Hall",
      website: "https://www.ccclerkrec.us/",
      websiteLabel: "Contra Costa County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "East Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Contra Costa acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many East Bay applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the SF regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Contra Costa families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "East Bay travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Concord." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Concord address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Concord before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Concord for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most East Bay travelers." },
    { q: "How fast can I renew my passport in Concord?", a: "Fast Passport Center offers fast passport renewal in Concord in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Concord in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Concord?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Concord residents." },
    { q: "Can I expedite a child passport in Concord?", a: "Yes. Fast Passport Center expedites child passports in Concord for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Concord with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Concord?",
      a: "With Fast Passport Center's emergency courier service, Concord travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Concord?",
      a: "Popular options include the Concord Post Office (94519), Walnut Creek Post Office (94596), Pleasant Hill Post Office (94523), and Contra Costa County Clerk (94553). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Concord?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in East Contra Costa or near Brentwood?",
      a: "We serve all of Contra Costa County including Antioch, Brentwood, Oakley, and Pittsburg. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Concord?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Concord?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Concord, Walnut Creek, and Pleasant Hill offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Concord?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Concord?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Contra Costa County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Concord", address: "2250 Oak Grove Rd, Concord, CA 94520", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Walnut Creek", address: "1350 N Main St, Walnut Creek, CA 94596", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Concord", address: "2075 Diamond Blvd, Concord, CA 94520", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Pleasant Hill", address: "1550 Contra Costa Blvd, Pleasant Hill, CA 94523", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Antioch", address: "4893 Lone Tree Way, Antioch, CA 94531", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Martinez", address: "640 Court St, Martinez, CA 94553", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — San Ramon", address: "2005 Crow Canyon Pl, San Ramon, CA 94583", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Concord", address: "2400 Monument Blvd, Concord, CA 94520", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Concord",
      address: "2070 Diamond Blvd, Concord, CA 94520",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Walnut Creek",
      address: "1515 N Main St, Walnut Creek, CA 94596",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Pleasanton",
      address: "6300 W Las Positas Blvd, Pleasanton, CA 94588",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Pittsburg",
      address: "4200 Delta Fair Blvd, Pittsburg, CA 94565",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for eastern Contra Costa County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "25 miles from Concord",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Closest major airport for East Bay travelers. International flights to Mexico, Canada, and Europe. Major domestic hub.",
      flightUrl: "https://www.flyoakland.com/",
      flightLabel: "Oakland Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "40 miles from Concord",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-80.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "45 miles from Concord",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Convenient for Silicon Valley and South Bay destinations. Domestic flights with some international connections.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
  ],
};